import { useState } from 'react';
import { Check, ChevronsUpDown, FolderOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Project } from '@/lib/api/projects';
import { Badge } from '@/components/ui/badge';

interface ProjectSelectorProps {
  projects: Project[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  loading?: boolean;
}

export default function ProjectSelector({
  projects,
  selectedId,
  onSelect,
  loading = false,
}: ProjectSelectorProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const selectedProject = projects.find((p) => p.id === selectedId);

  if (loading) {
    return (
      <div className="flex-1 max-w-md">
        <div className="h-10 bg-muted animate-pulse rounded-lg"></div>
      </div>
    );
  }

  const filteredProjects = projects.filter((project) => {
    const searchLower = search.toLowerCase();
    return (
      project.name.toLowerCase().includes(searchLower) ||
      project.urlSlug.toLowerCase().includes(searchLower)
    );
  });

  const handleSelect = (projectId: string) => {
    onSelect(projectId);
    setOpen(false);
    setSearch('');
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="flex-1 max-w-md justify-between"
        >
          {selectedProject ? (
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <FolderOpen className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <span className="truncate">{selectedProject.name}</span>
              <Badge variant="secondary" className="ml-auto flex-shrink-0">
                {selectedProject.mode === 'power' ? 'Power' : 'Lite'}
              </Badge>
            </div>
          ) : (
            <span className="text-muted-foreground">Select a project...</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0" align="start">
        <div className="flex flex-col max-h-[400px]">
          {/* Search Input */}
          <div className="p-2 border-b">
            <input
              type="text"
              placeholder="Search projects..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full px-3 py-2 text-sm rounded-md border bg-background focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>

          {/* Projects List */}
          <div className="overflow-y-auto">
            {filteredProjects.length === 0 ? (
              <div className="p-4 text-center text-sm text-muted-foreground">
                No projects found
              </div>
            ) : (
              <div className="p-1">
                {filteredProjects.map((project) => (
                  <button
                    key={project.id}
                    onClick={() => handleSelect(project.id)}
                    className={`w-full flex items-center gap-2 px-2 py-3 rounded-md hover:bg-accent transition-colors ${
                      selectedId === project.id ? 'bg-accent' : ''
                    }`}
                  >
                    <Check
                      className={`h-4 w-4 flex-shrink-0 ${
                        selectedId === project.id ? 'opacity-100' : 'opacity-0'
                      }`}
                    />
                    <FolderOpen className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    <div className="flex-1 min-w-0 text-left">
                      <div className="font-medium text-sm truncate">{project.name}</div>
                      <div className="text-xs text-muted-foreground truncate">
                        {project.urlSlug}
                      </div>
                    </div>
                    <Badge variant="secondary" className="flex-shrink-0">
                      {project.mode === 'power' ? 'Power' : 'Lite'}
                    </Badge>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
